import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import { PrinterJobs } from "./printer_jobs";
import { UpperPanel, UpperPanelBtn } from "./uppre_pannel";
import { PrinterList } from "./printer_list";
import {
  ArrowBack,
  PrintDisabled,
  PrintDisabledTwoTone,
} from "@material-ui/icons";
import { getPost, getRequest } from "../utils/commHelper";
import { CancelReasonModal } from "./cancel_reason_modal";
import { mapJobFromServer } from "./printerMappers";
import {
  DEFAULT_DATE_RANGE,
  buildPrinterJobsRequestPath,
  mergeLiveJobsIntoPrinterJobs,
} from "./printerJobsQuery";
import { theme } from "./theme";

export const cancelJob = async ({
  jobSelected,
  balanceReasonId,
  isDev = false,
}) => {
  if (!jobSelected?.id) return;
  if (!balanceReasonId) {
    throw new Error("balanceReasonId is required");
  }
  await getPost(
    "/dashboardCancelPrintJob",
    {
      printJobsId: jobSelected.id,
      balanceReasonId,
    },
    isDev
  );
};

export const clearAllPrintJobs = async ({
  printer,
  newAction = "clear",
  isDev = false,
}) => {
  const printerId = printer?.printer_id || printer?.id;
  if (!printerId) return;
  await getPost(
    "/restartPrinter",
    {
      printerId,
      action: newAction,
    },
    isDev
  );
};

export const cancelAllInQueue = async ({
  printer,
  balanceReasonId,
  isDev = false,
}) => {
  const printerId = printer?.printer_id || printer?.id;
  // Defense-in-depth: the server only scopes this to one printer's queue when
  // `printerId` is present in the body — omit it and the server cancels every
  // in-queue job across every printer/building instead. The UI only ever shows
  // this button once a printer is selected, but this guard is what actually
  // stops an unscoped request from ever leaving the client, rather than
  // relying solely on that UI state being correct.
  if (!printerId) {
    throw new Error("printerId is required");
  }
  if (!balanceReasonId) {
    throw new Error("balanceReasonId is required");
  }
  await getPost(
    "/dashboardCancelAllInQueue",
    {
      printerId,
      balanceReasonId,
    },
    isDev
  );
};

const PrinterListPage = ({
  printers,
  jobs: globalJobs,
  isDev = false,
  printerToOpen = null,
  onPrinterToOpenConsumed,
}) => {
  const [jobs, setJobs] = useState();
  const [printer, setPrinter] = useState();
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [jobsLoading, setJobsLoading] = useState(false);
  // Monotonic counter so a slow response for a previously selected printer or
  // range can never overwrite the page of the one currently on screen.
  const jobsRequestSequence = useRef(0);
  // Latest socket-fed global snapshot, readable from inside the fetch callbacks
  // without making every socket event re-run the fetch.
  const latestGlobalJobsRef = useRef(globalJobs);
  useEffect(() => {
    latestGlobalJobsRef.current = globalJobs;
  }, [globalJobs]);
  const [disableResetButton, setDisableResetButton] = useState(false);
  const [cancelReasons, setCancelReasons] = useState([]);
  const [cancelModal, setCancelModal] = useState(null); // { type: 'queue' } | null

  // Every way of choosing (or leaving) a printer goes through here so a newly
  // opened printer always starts from the plain "latest" page, not a date range
  // left over from investigating a different room.
  const selectPrinter = (nextPrinter) => {
    setPrinter(nextPrinter);
    setDateRange(DEFAULT_DATE_RANGE);
  };

  // Deep-link from the jobs list: a room name was clicked, open that printer's
  // page directly. Consume-once so the back arrow / manual selection afterwards
  // aren't overridden by a stale prop.
  useEffect(() => {
    if (!printerToOpen?.printer_id) return;
    selectPrinter(printers?.[printerToOpen.printer_id] || printerToOpen);
    onPrinterToOpenConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printerToOpen]);

  useEffect(() => {
    getRequest("/printCancelReasons", isDev)
      .then((res) => {
        const payload = res?.data?.result ?? res?.data ?? [];
        setCancelReasons(Array.isArray(payload) ? payload : []);
      })
      .catch((e) => console.error("[Printer] load cancel reasons", e));
  }, [isDev]);

  // The printer page fetches its own page of jobs, scoped to this printer and the
  // chosen date range. The global snapshot only ever holds the latest ~100 jobs
  // across ALL printers (roughly today), which is not enough to investigate a
  // ticket about last week — see the Slack request behind this. Live socket
  // updates are folded in by the effect below.
  useEffect(() => {
    if (!printer) {
      setJobs(undefined);
      setJobsLoading(false);
      return;
    }
    const printerId = printer.printer_id;
    const requestId = ++jobsRequestSequence.current;
    setJobsLoading(true);
    getRequest(
      buildPrinterJobsRequestPath({
        printerId,
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
      }),
      isDev
    )
      .then((res) => {
        if (requestId !== jobsRequestSequence.current) return;
        const payload = res?.data?.result ?? res?.data ?? [];
        const page = Array.isArray(payload)
          ? payload.map(mapJobFromServer).filter(Boolean)
          : [];
        // Anything the socket delivered for this printer while the request was
        // in flight would otherwise be lost until its next status change.
        const liveForPrinter = (latestGlobalJobsRef.current || []).filter(
          (job) => job.printer_id === printerId
        );
        setJobs(mergeLiveJobsIntoPrinterJobs(page, liveForPrinter, dateRange));
      })
      .catch((e) => {
        if (requestId !== jobsRequestSequence.current) return;
        console.error("[Printer] load printer jobs failed", e);
        // Degrade to whatever the global snapshot already holds for this printer
        // rather than showing an empty page.
        setJobs(
          (latestGlobalJobsRef.current || []).filter(
            (job) => job.printer_id === printerId
          )
        );
      })
      .finally(() => {
        if (requestId === jobsRequestSequence.current) setJobsLoading(false);
      });
  }, [printer, dateRange, isDev]);

  // Live updates: a job already on the page is always refreshed (status changes
  // must show), a job new to the page is added only when it falls inside the
  // selected range, so a "30 days" view does not keep growing with today's jobs.
  useEffect(() => {
    if (!printer) return;
    const printerId = printer.printer_id;
    const liveForPrinter = (globalJobs || []).filter(
      (job) => job.printer_id === printerId
    );
    if (!liveForPrinter.length) return;
    setJobs((current) =>
      current ? mergeLiveJobsIntoPrinterJobs(current, liveForPrinter, dateRange) : current
    );
  }, [printer, globalJobs, dateRange]);

  return (
    <Wrapper>
      <UpperPanel>
        {printer ? (
          <UpperPanelBtn onClick={() => selectPrinter(undefined)}>
            <ArrowBack style={{ fontSize: 18, pointerEvents: "none" }} />
          </UpperPanelBtn>
        ) : null}
        {printer ? (
          <UpperPanelBtn
            disabled={disableResetButton}
            onClick={async () => {
              if (disableResetButton) return;
              setDisableResetButton(true);
              setTimeout(() => setDisableResetButton(false), 5000);
              try {
                await clearAllPrintJobs({
                  printer,
                  newAction: "clear",
                  isDev,
                });
              } catch (e) {
                console.error(e);
                window.alert(e?.response?.data?.error || e.message);
              }
            }}
          >
            <PrintDisabledTwoTone style={{ fontSize: 18, pointerEvents: "none" }} />
            {disableResetButton ? printer.action || "…" : "Clear jobs"}
          </UpperPanelBtn>
        ) : null}
        {printer ? (
          <UpperPanelBtn
            disabled={disableResetButton}
            color={theme.danger}
            backgroundColorHover={theme.dangerLight}
            onClick={async () => {
              if (disableResetButton) return;
              setDisableResetButton(true);
              setTimeout(() => setDisableResetButton(false), 5000);
              try {
                await clearAllPrintJobs({
                  printer,
                  newAction: "restart",
                  isDev,
                });
              } catch (e) {
                console.error(e);
                window.alert(e?.response?.data?.error || e.message);
              }
            }}
          >
            <PrintDisabled style={{ fontSize: 18, pointerEvents: "none" }} />
            {disableResetButton ? printer.action || "…" : "Clear & Restart"}
          </UpperPanelBtn>
        ) : null}
        {printer ? (
          <UpperPanelBtn
            color={theme.danger}
            backgroundColorHover={theme.dangerLight}
            onClick={() => setCancelModal({ type: "queue" })}
          >
            Cancel queue
          </UpperPanelBtn>
        ) : null}
      </UpperPanel>
      {printer ? (
        <PrinterJobs
          printers={printers}
          jobs={jobs}
          isDev={isDev}
          loading={jobsLoading}
          title={printer.room_name || printer.title || "Printer"}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />
      ) : (
        <PrinterList printers={printers} jobs={globalJobs} setPrinter={selectPrinter} />
      )}

      <CancelReasonModal
        open={!!cancelModal}
        reasons={cancelReasons}
        title="Cancel all jobs in queue"
        onClose={() => setCancelModal(null)}
        onConfirm={async (balanceReasonId) => {
          try {
            await cancelAllInQueue({ printer, balanceReasonId, isDev });
            setCancelModal(null);
          } catch (e) {
            console.error(e);
            window.alert(e?.response?.data?.error || e.message);
          }
        }}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export { PrinterListPage };
